import { useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

export function Question({ index, total, question, selected, dir, onAnswer, onBack }) {
  const reduce = useReducedMotion();
  const headingRef = useRef(null);

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, [index]);

  return (
    <main data-testid="question-screen" className="pt-10 sm:pt-14">
      <div>
        <p className="text-[12px] font-semibold uppercase tracking-[0.22em] text-teal">
          Întrebarea {index + 1} din {total}
        </p>
        <div
          role="progressbar"
          aria-label="Progres quiz"
          aria-valuemin={1}
          aria-valuemax={total}
          aria-valuenow={index + 1}
          data-testid="progress-bar"
          className="mt-4 h-[4px] w-full overflow-hidden rounded-full bg-white/10"
        >
          <motion.div
            data-testid="progress-bar-fill"
            className="h-full rounded-full bg-gradient-to-r from-teal to-blue"
            initial={false}
            animate={{ width: `${((index + 1) / total) * 100}%` }}
            transition={{ duration: 0.45, ease: EASE }}
          />
        </div>
      </div>

      <motion.div
        key={index}
        initial={reduce ? false : { opacity: 0, x: 24 * dir }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
      >
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="mt-8 font-display text-[1.7rem] font-bold leading-[1.25] outline-none sm:text-[2.1rem]"
        >
          {question.q}
        </h1>

        <ul className="mt-7 space-y-3">
          {question.options.map((option, i) => (
            <li key={i}>
              <motion.button
                data-testid={`quiz-option-${i}`}
                onClick={() => onAnswer(i)}
                whileTap={{ scale: 0.985 }}
                className={`flex w-full min-h-[56px] items-start gap-3 rounded-2xl border px-5 py-4 text-left text-base leading-relaxed text-[#F7FAFC] transition-[background-color,border-color,box-shadow] duration-200 ${
                  selected === i
                    ? "glow-soft border-tealbright bg-selectbg text-white"
                    : "border-teal/40 bg-answerbg hover:border-teal hover:bg-hoverbg hover:shadow-[0_0_14px_rgba(32,214,199,0.12)]"
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg font-display text-xs font-bold ${
                    selected === i ? "bg-white/10 text-tealbright" : "bg-teal/15 text-teal"
                  }`}
                >
                  {String.fromCharCode(65 + i)}
                </span>
                <span className="font-medium">{option.t}</span>
              </motion.button>
            </li>
          ))}
        </ul>

        <button
          data-testid="back-button"
          onClick={onBack}
          className="mt-8 inline-flex min-h-[52px] items-center gap-2 rounded-full px-3 text-[15px] text-teal transition-colors duration-200 hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Înapoi
        </button>
      </motion.div>
    </main>
  );
}
